import api from "@api";
import type { Product } from "@type/product";
import { fetchCatalog } from "@provider/catalog-cache";
import { queryClient } from "@provider/query-client";
import type { IssuerResponse, OriginalCategoryType } from "@type/global";
import type { Statiegeld } from "@type/statiegeld";

// Hoe lang data vers is. Binnen die tijd geen nieuwe request bij mount of bij
// terugkeren naar de app (refetchOnWindowFocus respecteert staleTime).
const CATALOG_STALE_TIME = 60 * 1000;
const STATIEGELD_STALE_TIME = 60 * 1000;
const ISSUERS_STALE_TIME = 60 * 60 * 1000;
const DELIVERY_AREA_STALE_TIME = 5 * 60 * 1000;

export const statiegeldQuery = {
  queryKey: ["statiegeld"],
  queryFn: () => api<Statiegeld[]>({ path: "/account/statiegeld" }),
  throwOnError: false,
  staleTime: STATIEGELD_STALE_TIME,
  refetchOnWindowFocus: true,
};
export const prefetchStatiegeld = () => queryClient.prefetchQuery({ ...statiegeldQuery });

// Producten en categorieën komen uit de lokale catalogus-cache
// (catalog-cache.ts) en worden met If-None-Match gevalideerd.
export const categoriesQuery = {
  queryKey: ["categories"],
  queryFn: () => fetchCatalog<OriginalCategoryType[]>("categories", "/categories"),
  throwOnError: false,
  staleTime: CATALOG_STALE_TIME,
  refetchOnWindowFocus: true,
};

export const prefetchCategories = () => queryClient.prefetchQuery({ ...categoriesQuery });

export const productsQuery = {
  queryKey: ["products"],
  queryFn: () => fetchCatalog<Product[]>("products", "/products/v2/all"),
  throwOnError: false,
  staleTime: CATALOG_STALE_TIME,
  refetchOnWindowFocus: true,
};

export const prefetchProducts = () => queryClient.prefetchQuery({ ...productsQuery });

// Betaalbanken: alleen nodig op de account- en betaalschermen, dus geen
// prefetch bij het opstarten.
export const issuersQuery = {
  queryKey: ["issuers"],
  queryFn: () => api<IssuerResponse>({ path: "/issuer-list" }),
  throwOnError: false,
  staleTime: ISSUERS_STALE_TIME,
};

// Waar de klant machtigt (GET /mandate-provider): "emandates" (bankenlijst in
// de app, PDF-formulier voor BE) of "mollie" (één knop; bank kiezen bij
// Mollie). Een server zonder dit endpoint of een fout = "emandates", de
// bestaande UI.
export type MandateProvider = "emandates" | "mollie";
export const mandateProviderQuery = {
  queryKey: ["mandate-provider"],
  queryFn: async (): Promise<MandateProvider> => {
    const res = await api<{ provider?: string }>({ path: "/mandate-provider" });
    return res?.provider === "mollie" ? "mollie" : "emandates";
  },
  throwOnError: false,
  staleTime: 5 * 60 * 1000,
};
export const prefetchMandateProvider = () => queryClient.prefetchQuery({ ...mandateProviderQuery });

export type DeliveryArea = { nl: string[]; be: string[] };
// Bezorggebied van de server (GET /delivery-area); zonder antwoord valt de
// app terug op de ingebouwde lijst in data.ts.
export const deliveryAreaQuery = {
  queryKey: ["delivery-area"],
  queryFn: () => api<DeliveryArea>({ path: "/delivery-area" }),
  throwOnError: false,
  staleTime: DELIVERY_AREA_STALE_TIME,
};
export const prefetchDeliveryArea = () => queryClient.prefetchQuery({ ...deliveryAreaQuery });
